'use client';

/* eslint-disable @next/next/no-img-element */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ImgHTMLAttributes } from 'react';

interface BestsellerFallbackImageProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  sources: Array<string | undefined>;
  alt: string;
}

export default function BestsellerFallbackImage({
  sources,
  alt,
  onError,
  ...props
}: BestsellerFallbackImageProps) {
  const candidates = useMemo(
    () => Array.from(new Set(sources.filter((source): source is string => Boolean(source)))),
    [sources],
  );
  const sourceKey = candidates.join('|');
  const [sourceIndex, setSourceIndex] = useState(0);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setSourceIndex(0);
  }, [sourceKey]);

  const src = candidates[sourceIndex];
  const showNextSource = useCallback(() => {
    setSourceIndex((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!src) return;
    const image = imageRef.current;
    if (image?.complete && image.naturalWidth === 0) showNextSource();
  }, [showNextSource, src]);

  if (!src) return null;

  return (
    // The error listener advances through backend-provided image candidates.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <img
      {...props}
      ref={imageRef}
      src={src}
      alt={alt}
      onError={(event) => {
        onError?.(event);
        showNextSource();
      }}
    />
  );
}
